import React, { useState } from 'react';
import {
    Grid, TextField, MenuItem, Button, Box, Typography, Paper, IconButton,
    Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs, Tab, Divider, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';

const EmployeeExpenseBulkForm = ({
    formData, sites, departments, expenseCategories,
    filteredExpenseCategories, setFilteredExpenseCategories,
    allFieldSettings, onItemsChange, onFormDataChange
}) => {
    const [items, setItems] = useState([]);
    const [currentItem, setCurrentItem] = useState({
        employeeName: '', expenseCategoryId: '', amount: '', paymentSource: 'COMPANY',
        remarks: '', entryDate: new Date().toISOString().split('T')[0],
        billStatus: 'received', billPendingFrom: ''
    });
    const [error, setError] = useState('');
    const [entryMode, setEntryMode] = useState('manual');
    const [excelError, setExcelError] = useState('');
    const [excelSuccess, setExcelSuccess] = useState('');

    const employeeExpenseCategories = expenseCategories.filter(
        cat => allFieldSettings?.[cat.id]?.formType === 'employee_expense'
    );

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        onFormDataChange({ target: { name, value } });
        if (name === 'departmentId') {
            setFilteredExpenseCategories(expenseCategories);
        }
    };

    const handleCurrentItemChange = (e) => {
        const { name, value } = e.target;
        setCurrentItem(prev => ({ ...prev, [name]: value }));
    };

    const handleAddItem = () => {
        if (!currentItem.employeeName) { setError('Employee Name is required'); return; }
        if (!currentItem.expenseCategoryId) { setError('Expense Category is required'); return; }
        if (!currentItem.amount || parseFloat(currentItem.amount) <= 0) { setError('Valid Amount is required'); return; }
        if (!currentItem.entryDate) { setError('Date is required'); return; }
        if (currentItem.billStatus === 'pending' && !currentItem.billPendingFrom) { setError('Please specify who the invoice is pending from'); return; }

        const category = expenseCategories.find(c => c.id === parseInt(currentItem.expenseCategoryId));
        const newItem = {
            ...currentItem,
            id: Date.now(),
            expenseCategoryName: category?.category_name
        };
        const updated = [...items, newItem];
        setItems(updated);
        onItemsChange(updated);
        setCurrentItem({
            employeeName: '', expenseCategoryId: currentItem.expenseCategoryId, amount: '',
            paymentSource: 'COMPANY', remarks: '', entryDate: currentItem.entryDate,
            billStatus: 'received', billPendingFrom: ''
        });
        setError('');
    };

    const handleRemoveItem = (id) => {
        const updated = items.filter(i => i.id !== id);
        setItems(updated);
        onItemsChange(updated);
    };

    const calculateTotal = () => items.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);

    const handleDownloadTemplate = () => {
        const wb = XLSX.utils.book_new();
            const headers = ['Supplier Name', 'Expense Category (Food Expenses / Imprest)', 'Amount', 'Payment Source (COMPANY/SUPERVISOR)', 'Remarks', 'Date (DD-MM-YYYY)'];
        const sampleRow = ['Ramesh Kumar', 'Food Expenses', '850', 'COMPANY', 'Lunch for site visit', '01-09-2026'];
        const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
        ws['!cols'] = headers.map(() => ({ wch: 28 }));
        XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
        XLSX.writeFile(wb, 'bulk_employee_expense_template.xlsx');
    };

    const parseExcelDate = (val) => {
        if (val instanceof Date) return val.toISOString().split('T')[0];
        if (typeof val === 'number') {
            const d = XLSX.SSF.parse_date_code(val);
            return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
        }
        const str = String(val || '').trim();
        const parts = str.split(/[-/]/);
        if (parts.length === 3 && parts[0].length <= 2) {
            // DD-MM-YYYY
            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
        return str;
    };

    const handleExcelUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setExcelError(''); setExcelSuccess('');
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
                if (data.length < 2) { setExcelError('Excel file is empty or has no data rows'); return; }
                const newItems = []; const errors = [];
                const today = new Date().toISOString().split('T')[0];
                for (let i = 1; i < data.length; i++) {
                    const row = data[i];
                    if (!row || row.length === 0 || !row[0]) continue;
                    const employeeName = String(row[0] || '').trim();
                    const categoryName = String(row[1] || '').trim();
                    const amount = parseFloat(row[2]);
                    const paymentSource = String(row[3] || 'COMPANY').trim().toUpperCase();
                    const remarks = String(row[4] || '').trim();
                    const entryDate = parseExcelDate(row[5]);

                    if (!employeeName) { errors.push(`Row ${i + 1}: Employee Name is required`); continue; }
                    const category = expenseCategories.find(c => c.category_name.toLowerCase() === categoryName.toLowerCase());
                    if (!category) { errors.push(`Row ${i + 1}: Expense Category "${categoryName}" not found`); continue; }
                    if (isNaN(amount) || amount <= 0) { errors.push(`Row ${i + 1}: Invalid amount`); continue; }
                    if (!['COMPANY', 'SUPERVISOR'].includes(paymentSource)) { errors.push(`Row ${i + 1}: Payment Source must be COMPANY or SUPERVISOR`); continue; }
                    if (!entryDate || isNaN(Date.parse(entryDate))) { errors.push(`Row ${i + 1}: Invalid date`); continue; }
                    if (entryDate > today) { errors.push(`Row ${i + 1}: Date cannot be in the future`); continue; }

                    newItems.push({
                        id: Date.now() + i, employeeName, expenseCategoryId: category.id,
                        expenseCategoryName: category.category_name, amount: amount.toString(),
                        paymentSource, remarks, entryDate,
                        billStatus: 'received', billPendingFrom: ''
                    });
                }
                if (errors.length > 0) setExcelError(`Some rows had errors:\n${errors.join('\n')}`);
                if (newItems.length > 0) {
                    const updated = [...items, ...newItems];
                    setItems(updated); onItemsChange(updated);
                    setExcelSuccess(`✅ ${newItems.length} entr${newItems.length === 1 ? 'y' : 'ies'} imported from Excel!`);
                }
            } catch (err) { setExcelError('Failed to read Excel file. Please use the template format.'); }
        };
        reader.readAsArrayBuffer(file);
        e.target.value = '';
    };

    return (
        <Grid container spacing={2}>
            {error && <Grid item xs={12}><Alert severity="error" onClose={() => setError('')}>{error}</Alert></Grid>}

            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>📋 Site & Department</Typography>
                <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Site *" name="siteId" value={formData.siteId} onChange={handleFormChange} required>
                    {sites.map((site) => (<MenuItem key={site.id} value={site.id}>{site.siteName}</MenuItem>))}
                </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Department *" name="departmentId" value={formData.departmentId} onChange={handleFormChange} required>
                    {departments.map((dept) => (<MenuItem key={dept.id} value={dept.id}>{dept.department_name}</MenuItem>))}
                </TextField>
            </Grid>

            <Grid item xs={12} sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main' }}>🧾 Add Expense Entries</Typography>
                    <Tabs value={entryMode} onChange={(e, v) => setEntryMode(v)}>
                        <Tab label="Manual Entry" value="manual" />
                        <Tab label="📊 Excel Upload" value="excel" />
                    </Tabs>
                </Box>
                <Divider sx={{ mb: 2 }} />
            </Grid>

            {entryMode === 'excel' && (
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Upload Expenses via Excel</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Download the template, fill in each employee's expense (each row can have its own date), and upload.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>Download Template</Button>
                            <Button variant="contained" component="label" startIcon={<UploadIcon />}>
                                Upload Excel / CSV File
                                <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleExcelUpload} />
                            </Button>
                        </Box>
                        {excelError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setExcelError('')}><pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>{excelError}</pre></Alert>}
                        {excelSuccess && <Alert severity="success" onClose={() => setExcelSuccess('')}>{excelSuccess}</Alert>}
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Expense Category must exactly match an existing category name (e.g. "Food Expenses" or "Imprest").
                        </Alert>
                    </Paper>
                </Grid>
            )}

            {entryMode === 'manual' && (
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                 <TextField fullWidth label="Supplier Name *" name="employeeName" value={currentItem.employeeName} onChange={handleCurrentItemChange} />
               </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField select fullWidth label="Expense Category *" name="expenseCategoryId" value={currentItem.expenseCategoryId} onChange={handleCurrentItemChange} disabled={!formData.departmentId} helperText={!formData.departmentId ? 'Select department first' : ''}>
                                    {employeeExpenseCategories.map((cat) => (<MenuItem key={cat.id} value={cat.id}>{cat.category_name}</MenuItem>))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth type="number" label="Amount *" name="amount" value={currentItem.amount} onChange={handleCurrentItemChange} inputProps={{ min: 0, step: 0.01 }} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField select fullWidth label="Payment Source *" name="paymentSource" value={currentItem.paymentSource} onChange={handleCurrentItemChange}>
                                    <MenuItem value="COMPANY">Company</MenuItem>
                                    <MenuItem value="SUPERVISOR">Supervisor</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth type="date" label="Date *" name="entryDate" value={currentItem.entryDate} onChange={handleCurrentItemChange} InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().split('T')[0] }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Remarks (Optional)" name="remarks" value={currentItem.remarks} onChange={handleCurrentItemChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem} fullWidth disabled={!formData.departmentId}>
                                    Add Entry to List
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            )}

            {items.length > 0 && (
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Added Entries ({items.length})</Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Supplier Name</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Payment</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.employeeName}</TableCell>
                                        <TableCell>{item.expenseCategoryName}</TableCell>
                                        <TableCell align="right">₹{parseFloat(item.amount).toLocaleString()}</TableCell>
                                        <TableCell>{item.paymentSource}</TableCell>
                                        <TableCell>{item.entryDate}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.id)}><DeleteIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={2} align="right"><Typography fontWeight="bold">Total:</Typography></TableCell>
                                    <TableCell align="right"><Typography fontWeight="bold" color="primary">₹{calculateTotal().toLocaleString()}</Typography></TableCell>
                                    <TableCell colSpan={4} />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            )}

            {items.length === 0 && (
                <Grid item xs={12}><Alert severity="warning">Please add at least one entry before submitting</Alert></Grid>
            )}
        </Grid>
    );
};

export default EmployeeExpenseBulkForm;